import { ApiError } from './client'

const BASE_URL = import.meta.env.VITE_API_BASE_URL || '/v1'

// Exported so sibling admin-surface modules (e.g. `activation.ts`) share one
// transport instead of each re-deriving the `adminkey` header handling.
//
// Throws `ApiError` (which extends Error, so existing `catch (e)` callers are
// unaffected) rather than a bare Error, so callers can branch on `.status`.
// The activation contracts screen needs this: `GET /admin/activation/contracts`
// isn't deployed on the real BE yet, and a 404 there means "route missing",
// which must render differently from "no contracts exist". Without a status
// the only alternative is string-matching the error text.
export async function adminFetch<T>(
  path: string,
  adminKey: string,
  options: RequestInit = {},
): Promise<T> {
  const response = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      adminkey: adminKey,
      ...options.headers,
    },
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throw new ApiError(
      response.status,
      error.message || `Admin request failed (${response.status})`,
    )
  }

  if (response.status === 204) return undefined as T
  return response.json()
}

/** Binary sibling of `adminFetch` for endpoints that stream a file back
 *  (`GET /admin/activation/contracts/:id/pdf`). Anchors can't carry the
 *  `adminkey` header, so downloads must go through fetch → Blob. The
 *  filename comes from the BE's `Content-Disposition` (res.download sets
 *  it), so the saved file keeps the server's canonical name. */
export async function adminFetchBlob(
  path: string,
  adminKey: string,
): Promise<{ blob: Blob; filename?: string }> {
  const response = await fetch(`${BASE_URL}${path}`, {
    headers: { adminkey: adminKey },
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throw new ApiError(
      response.status,
      error.message || `Admin request failed (${response.status})`,
    )
  }

  const disposition = response.headers.get('content-disposition') ?? ''
  const filename = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(disposition)?.[1]?.trim()
  return { blob: await response.blob(), filename }
}

export interface AdminUser {
  id: number
  email: string
  firstName?: string
  lastName?: string
  admin: boolean
  isActive: boolean
  createdAt: string
  updatedAt: string
  metadata: Record<string, unknown>
}

export interface AdminUsersPage {
  items: AdminUser[]
  total: number
  limit: number
  offset: number
}

// ── GoHighLevel connection (admin/ghl on the real BE, branch `ghl`) ──
// The flow settled in the Aug 24 dev meet: the agency duplicates the GHL
// template account FIRST, then an admin pastes the new location id + PIT
// token here. Init registers a background job that ensures the ETX pipeline,
// estimate-status stages, opportunity custom fields, and primary calendar
// exist, then backfills existing customers as GHL contacts.

export interface InitGhlAccountDto {
  teamId: number
  locationId: string
  /** GoHighLevel Private Integration Token. Never stored or echoed back. */
  pitToken: string
  /** Defaults to true on the BE when omitted. */
  enabled?: boolean
  /** API base URL override; LeadConnector default when omitted. */
  baseUrl?: string
}

export interface GhlCalendarsResponse {
  teamId: number
  calendars: Record<string, unknown>[]
}

export const adminApi = {
  verifyKey(adminKey: string) {
    return adminFetch<{ status: string }>('/admin/health', adminKey)
      .then(() => true)
      .catch(() => false)
  },

  ghlInit(adminKey: string, dto: InitGhlAccountDto) {
    return adminFetch<{ jobId: string }>('/admin/ghl/init', adminKey, {
      method: 'POST',
      body: JSON.stringify(dto),
    })
  },

  ghlBackfillContacts(adminKey: string, teamId: number) {
    return adminFetch<{ jobId: string | null }>('/admin/ghl/backfill-contacts', adminKey, {
      method: 'POST',
      body: JSON.stringify({ teamId }),
    })
  },

  /** Lists the connected location's calendars — the cheapest end-to-end
   *  probe that the stored account actually talks to GoHighLevel. */
  ghlCalendars(adminKey: string, teamId: number) {
    return adminFetch<GhlCalendarsResponse>(`/admin/ghl/${teamId}/calendars`, adminKey)
  },

  signInAs(adminKey: string, email: string) {
    return adminFetch<{ accessToken: string }>('/admin/sign-in-as', adminKey, {
      method: 'POST',
      body: JSON.stringify({ email }),
    })
  },

  triggerWorkerHealth(adminKey: string) {
    return adminFetch<{ jobId: string }>('/admin/workers/health', adminKey, {
      method: 'POST',
    })
  },

  listUsers(adminKey: string, params: { limit?: number; offset?: number } = {}) {
    const qs = new URLSearchParams()
    if (params.limit !== undefined) qs.set('limit', String(params.limit))
    if (params.offset !== undefined) qs.set('offset', String(params.offset))
    const suffix = qs.toString() ? `?${qs}` : ''
    return adminFetch<AdminUsersPage>(`/admin/users${suffix}`, adminKey)
  },
}
